<template>
  <div class="dtf" :class="{ 'dtf--open': open, 'dtf--compact': compact, 'dtf--invalid': showError }" ref="root">
    <div class="dtf-field" @click="focusInput">
      <span class="dtf-icon" aria-hidden="true"><i class="fas fa-calendar-day"></i></span>
      <input :id="id" ref="input" v-model="text" class="dtf-input" type="text" inputmode="numeric" autocomplete="off"
             placeholder="DD/MM/YYYY" :aria-invalid="dateInvalid ? 'true' : 'false'"
             @focus="focused = 'date'" @blur="onBlur" @keydown.enter.prevent="commit" @keydown.esc="close"
             @keydown.down.prevent="openPicker">
      <template v-if="withTime">
        <span class="dtf-sep" aria-hidden="true"></span>
        <input ref="time" v-model="timeText" class="dtf-time" type="text" inputmode="numeric" autocomplete="off"
               placeholder="HH:MM" maxlength="5" aria-label="Time" :aria-invalid="timeInvalid ? 'true' : 'false'"
               @click.stop @focus="focused = 'time'" @blur="onBlur" @keydown.enter.prevent="commit">
      </template>
      <button type="button" class="dtf-toggle" :aria-expanded="open ? 'true' : 'false'" aria-label="Choose from the calendar"
              @click.stop="open ? close() : openPicker()">
        <i class="fas fa-chevron-down"></i>
      </button>
    </div>
    <div v-if="showError || (hint && longDate)" class="dtf-hint">
      <span v-if="showError" class="dtf-error">{{ errorText }}</span>
      <span v-else>{{ longDate }}</span>
    </div>

    <div v-if="open" class="dtf-pop" :class="{ 'dtf-pop--up': up, 'dtf-pop--right': right }" role="dialog" aria-label="Calendar" @keydown.esc.stop="close">
      <div class="dtf-head">
        <button type="button" class="dtf-nav" aria-label="Previous month" @click="step(-1)"><i class="fas fa-chevron-left"></i></button>
        <select v-model.number="viewMonth" class="dtf-select" aria-label="Month">
          <option v-for="(m, i) in MONTHS" :key="m" :value="i">{{ m }}</option>
        </select>
        <select v-model.number="viewYear" class="dtf-select" aria-label="Year">
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
        <button type="button" class="dtf-nav" aria-label="Next month" @click="step(1)"><i class="fas fa-chevron-right"></i></button>
      </div>
      <div class="dtf-week">
        <span v-for="d in WEEKDAYS" :key="d">{{ d }}</span>
      </div>
      <div class="dtf-days">
        <button v-for="cell in cells" :key="cell.iso" type="button" class="dtf-day"
                :class="{ out: !cell.inMonth, today: cell.iso === todayIso, selected: cell.iso === selectedIso }"
                :aria-label="cell.label" :aria-pressed="cell.iso === selectedIso ? 'true' : 'false'" @click="pick(cell.iso)">
          {{ cell.day }}
        </button>
      </div>
      <div class="dtf-foot">
        <button type="button" class="dtf-link" @click="pick(todayIso)">Today</button>
        <button v-if="value" type="button" class="dtf-link muted" @click="clear">Clear</button>
      </div>
    </div>
  </div>
</template>

<script>
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const pad = (n) => String(n).padStart(2, '0');
const isoOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`; // m is 0-based
const daysIn = (y, m) => new Date(y, m + 1, 0).getDate();
const typedOf = (p) => `${pad(p.d)}/${pad(p.m + 1)}/${p.y}`;

/**
 * "YYYY-MM-DD", optionally followed by a time ("YYYY-MM-DDTHH:mm", "YYYY-MM-DD HH:mm:ss") -> { y, m (0-based), d, iso,
 * time: "HH:mm" | null }, or null. Never through Date parsing, which shifts by the time zone.
 */
function partsOf(v) {
  const hit = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(v || '');
  if (!hit) return null;
  const y = Number(hit[1]);
  const m = Number(hit[2]) - 1;
  const d = Number(hit[3]);
  if (!(m >= 0 && m < 12 && d >= 1 && d <= daysIn(y, m))) return null;
  return { y, m, d, iso: isoOf(y, m, d), time: hit[4] ? `${hit[4]}:${hit[5]}` : null };
}

/** What people type: 25/04/2018, 25-4-2018, 25.04.18, 25042018 or 2018-04-25 -> "YYYY-MM-DD", or null. */
function parseTyped(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  let y; let m; let d;
  let hit = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (hit) [, y, m, d] = hit.map(Number);
  else {
    hit = /^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{2}|\d{4})$/.exec(s) || /^(\d{2})(\d{2})(\d{4})$/.exec(s);
    if (!hit) return null;
    [, d, m, y] = hit.map(Number);
    if (y < 100) y += y > (new Date().getFullYear() % 100) + 5 ? 1900 : 2000;
  }
  const p = partsOf(isoOf(y, m - 1, d));
  return p ? p.iso : null;
}

/** 9:5, 09:05, 0905, 9.05 or 9h05 -> "HH:mm", or null. */
function parseTime(raw) {
  const hit = /^(\d{1,2})(?:[:.h]?(\d{2}))?$/.exec(String(raw || '').trim());
  if (!hit) return null;
  const h = Number(hit[1]);
  const mi = Number(hit[2] || 0);
  return h < 24 && mi < 60 ? `${pad(h)}:${pad(mi)}` : null;
}

/**
 * A date field in the app's own look (light and dark): type the date (day/month/year) or pick it from a calendar
 * whose month and year are dropdowns, so any year is one choice away.
 *   v-model    "YYYY-MM-DD" (or null); with `with-time` a time box is added and it is "YYYY-MM-DDTHH:mm"
 *   compact    the 38px look of the simulator's fields, without the long date underneath
 * A value is only sent when the date (or time) really changes, so an untouched field keeps its exact value.
 */
export default {
  name: 'DateField',
  props: {
    value: { type: String, default: null },
    id: { type: String, default: null },
    withTime: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    minYear: { type: Number, default: 1950 },
    maxYear: { type: Number, default: () => new Date().getFullYear() + 5 },
  },
  data() {
    const now = new Date();
    return {
      MONTHS,
      WEEKDAYS,
      text: '',
      timeText: '',
      focused: null, // 'date' | 'time' | null
      sentEmpty: false,
      open: false,
      up: false,
      right: false,
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(),
      todayIso: isoOf(now.getFullYear(), now.getMonth(), now.getDate()),
    };
  },
  computed: {
    hint() {
      return !this.compact;
    },
    dateInvalid() {
      return !!this.text.trim() && !parseTyped(this.text);
    },
    timeInvalid() {
      return this.withTime && !!this.timeText.trim() && !parseTime(this.timeText);
    },
    showError() {
      return !this.focused && (this.dateInvalid || this.timeInvalid);
    },
    errorText() {
      return this.dateInvalid
        ? 'Not a valid date: type it as day/month/year, e.g. 25/04/2018.'
        : 'Not a valid time: type it as hours:minutes, e.g. 09:30.';
    },
    selectedIso() {
      const p = partsOf(this.value);
      return p ? p.iso : null;
    },
    years() {
      const out = [];
      for (let y = this.maxYear; y >= this.minYear; y -= 1) out.push(y);
      return out;
    },
    longDate() {
      const p = partsOf(this.value);
      if (!p) return '';
      const day = new Date(p.y, p.m, p.d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      return this.withTime && p.time ? `${day} · ${p.time}` : day;
    },
    /** 6 weeks, Monday first, with the neighbouring months' days to fill the grid. */
    cells() {
      const first = new Date(this.viewYear, this.viewMonth, 1);
      const lead = (first.getDay() + 6) % 7;
      const out = [];
      for (let i = 0; i < 42; i += 1) {
        const date = new Date(this.viewYear, this.viewMonth, 1 - lead + i);
        out.push({
          iso: isoOf(date.getFullYear(), date.getMonth(), date.getDate()),
          day: date.getDate(),
          inMonth: date.getMonth() === this.viewMonth,
          label: date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        });
      }
      return out;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        // the empty value this field sent for a mistyped date: the text stays, with its error
        if (v === null && this.sentEmpty) {
          this.sentEmpty = false;
          return;
        }
        const p = partsOf(v);
        // while the person types, their text stays as it is (it may not be a whole date yet)
        if (this.focused !== 'date') this.text = p ? typedOf(p) : '';
        if (this.focused !== 'time') this.timeText = p && p.time ? p.time : '';
        if (p) this.showMonth(p.y, p.m);
      },
    },
    text() {
      // a whole date with a 4-digit year (or an emptied field) counts straight away, without having to leave the
      // field; "25/04/20" waits (it may become 2018), a 2-digit year counts on leaving the field or Enter
      const t = this.text.trim();
      const whole = /^\d{1,2}[-/.\s]\d{1,2}[-/.\s]\d{4}$|^\d{8}$|^\d{4}-\d{2}-\d{2}$/.test(t);
      if (this.focused === 'date' && ((whole && parseTyped(t)) || !t)) this.send();
    },
    timeText() {
      // a whole time (09:30, 0930) counts straight away; "9" waits for the rest, or for leaving the box
      if (this.focused === 'time' && /^\d{1,2}[:.h]\d{2}$|^\d{4}$/.test(this.timeText.trim()) && parseTime(this.timeText)) this.send();
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside, true);
  },
  methods: {
    focusInput() {
      this.$refs.input.focus();
    },
    /** Sends the typed date (and time) if it differs from the current value; a mistyped date sends null. */
    send() {
      // a mistyped time makes the whole value invalid, as a mistyped date does (an empty time box is 00:00)
      const timeBad = this.withTime && !!this.timeText.trim() && !parseTime(this.timeText);
      const iso = timeBad ? null : parseTyped(this.text);
      const time = this.withTime ? parseTime(this.timeText) || '00:00' : null;
      const now = partsOf(this.value);
      const same = iso && now && now.iso === iso && (!this.withTime || (now.time || '00:00') === time);
      if (same || (!iso && !this.value)) return;
      this.sentEmpty = !iso && (!!this.text.trim() || timeBad);
      this.$emit('input', iso ? (this.withTime ? `${iso}T${time}` : iso) : null);
    },
    showMonth(y, m) {
      this.viewYear = Math.min(this.maxYear, Math.max(this.minYear, y));
      this.viewMonth = m;
    },
    step(delta) {
      const date = new Date(this.viewYear, this.viewMonth + delta, 1);
      if (date.getFullYear() < this.minYear || date.getFullYear() > this.maxYear) return;
      this.showMonth(date.getFullYear(), date.getMonth());
    },
    openPicker() {
      const p = partsOf(this.value) || partsOf(parseTyped(this.text));
      if (p) this.showMonth(p.y, p.m);
      // near the bottom of the screen (the last field of a modal) the calendar opens upwards, if it fits there;
      // in a narrow column on the right it lines up with the field's right edge
      const box = this.$refs.root.getBoundingClientRect();
      const need = 380;
      // on a page the sticky header covers the top of the screen (a modal sits above it)
      const header = this.$el.closest('.modal') ? null : document.querySelector('.header-container');
      const topLimit = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      this.up = window.innerHeight - box.bottom < need && box.top - topLimit >= need;
      this.right = box.left + 300 > window.innerWidth - 12;
      this.open = true;
      document.addEventListener('mousedown', this.onOutside, true);
      // if it does not fit on screen, scroll the page (or the modal) just enough to show all of it
      this.$nextTick(() => {
        const pop = this.$el.querySelector('.dtf-pop');
        if (pop && pop.scrollIntoView) pop.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },
    close() {
      this.open = false;
      document.removeEventListener('mousedown', this.onOutside, true);
    },
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) this.close();
    },
    pick(iso) {
      this.text = typedOf(partsOf(iso));
      this.send();
      this.close();
      // with a time box, go on to the time; otherwise back to the date
      this.$nextTick(() => (this.withTime && !this.timeText ? this.$refs.time : this.$refs.input).focus());
    },
    clear() {
      this.text = '';
      this.timeText = '';
      this.$emit('input', null);
      this.close();
    },
    commit() {
      this.send();
      const p = partsOf(parseTyped(this.text));
      if (p) {
        this.text = typedOf(p);
        this.showMonth(p.y, p.m);
      }
      const t = parseTime(this.timeText);
      if (t) this.timeText = t;
    },
    onBlur() {
      this.focused = null;
      this.commit();
    },
  },
};
</script>

<style scoped lang="scss">
.dtf {
  position: relative;
}

.dtf-field {
  display: flex;
  align-items: center;
  height: 44px;
  border: 1.5px solid var(--border-color);
  border-radius: 12px;
  background: var(--bg-elevated);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  cursor: text;

  &:focus-within {
    border-color: var(--accent-green);
    box-shadow: 0 0 0 3px rgba(var(--accent-green-rgb), 0.18);
  }
}

.dtf--invalid .dtf-field {
  border-color: #d9534f;
  box-shadow: 0 0 0 3px rgba(217, 83, 79, 0.14);
}

.dtf-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-left: 7px;
  border-radius: 9px;
  color: var(--accent-green);
  background: rgba(var(--accent-green-rgb), 0.12);
  font-size: 0.85rem;
}

.dtf-input,
.dtf-time {
  height: 100%;
  padding: 0 10px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-main);
  font-size: 0.95rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;

  &::placeholder {
    color: var(--text-muted);
    opacity: 0.75;
  }
}

.dtf-input {
  flex: 1 1 auto;
  min-width: 0;
}

.dtf-time {
  flex: 0 0 78px;
  width: 78px;
  text-align: center;
}

.dtf-sep {
  flex: 0 0 1px;
  align-self: stretch;
  margin: 9px 0;
  background: var(--border-color);
}

.dtf-toggle {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  margin-right: 5px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.2s ease;

  &:hover {
    color: var(--accent-green);
    background: rgba(var(--accent-green-rgb), 0.1);
  }
}

.dtf-toggle,
.dtf-nav,
.dtf-day,
.dtf-link {
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--accent-green);
    outline-offset: 1px;
  }
}

.dtf--open .dtf-toggle {
  color: var(--accent-green);
  transform: rotate(180deg);
}

/* the simulator's fields: 38px, thinner border, bold value */
.dtf--compact {
  .dtf-field {
    height: 38px;
    border-width: 1px;
    background: var(--bg-secondary);

    &:focus-within {
      box-shadow: 0 0 0 3px rgba(var(--accent-green-rgb), 0.15);
    }
  }

  .dtf-icon {
    width: 26px;
    height: 26px;
    margin-left: 6px;
    border-radius: 8px;
    font-size: 0.78rem;
  }

  .dtf-input,
  .dtf-time {
    padding: 0 8px;
    color: var(--text-heading);
    font-weight: 700;
    font-size: 0.92rem;
  }

  .dtf-toggle {
    width: 30px;
    height: 30px;
    margin-right: 4px;
  }

  .dtf-pop {
    top: 44px;
  }
}

.dtf-hint {
  margin-top: 5px;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.dtf-error {
  color: #d9534f;
}

/* ---- the calendar */
.dtf-pop {
  position: absolute;
  z-index: 30;
  top: 50px;
  left: 0;
  width: 300px;
  max-width: calc(100vw - 24px);
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  background: var(--card-bg);
  box-shadow: var(--shadow-lg);
  font-weight: 400;
  animation: dtf-in 0.16s ease-out;
}

// (as specific as the compact rule above, and after it)
.dtf .dtf-pop--up {
  top: auto;
  bottom: calc(100% + 6px);
}

.dtf .dtf-pop--right {
  left: auto;
  right: 0;
}

@keyframes dtf-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

.dtf-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.dtf-nav {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    color: var(--accent-green);
    background: rgba(var(--accent-green-rgb), 0.1);
  }
}

.dtf-select {
  flex: 1 1 auto;
  min-width: 0;
  height: 32px;
  padding: 0 24px 0 10px;
  border: 1px solid var(--border-color);
  border-radius: 9px;
  // a small chevron drawn in the muted text colour (two half-squares)
  background-color: var(--bg-secondary);
  background-image: linear-gradient(45deg, transparent 50%, var(--text-muted) 50%), linear-gradient(135deg, var(--text-muted) 50%, transparent 50%);
  background-position: calc(100% - 14px) 55%, calc(100% - 9px) 55%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  color: var(--text-main);
  font-size: 0.86rem;
  font-weight: 700;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  color-scheme: light; // the open list follows the theme

  &:last-of-type {
    flex: 0 0 84px;
  }

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }
}

[data-theme="dark"] .dtf-select {
  color-scheme: dark;
}

.dtf-week,
.dtf-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.dtf-week span {
  padding: 4px 0;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.dtf-day {
  height: 36px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: var(--text-main);
  font-size: 0.86rem;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: rgba(var(--accent-green-rgb), 0.12);
    color: var(--text-heading);
  }

  &.out {
    color: var(--text-muted);
    opacity: 0.45;
  }

  &.today {
    border-color: rgba(var(--accent-green-rgb), 0.55);
    color: var(--accent-green);
    font-weight: 700;
  }

  &.selected {
    border-color: transparent;
    background: var(--accent-gradient);
    color: #fff;
    font-weight: 700;
    opacity: 1;
    box-shadow: 0 6px 14px -6px var(--accent-glow);
  }
}

.dtf-foot {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-color);
}

.dtf-link {
  border: none;
  background: transparent;
  padding: 4px 6px;
  border-radius: 8px;
  color: var(--accent-green);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(var(--accent-green-rgb), 0.1);
  }

  &.muted {
    color: var(--text-muted);
  }
}
</style>
